/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { ReactNode, useState } from 'react';
import { InfoCard } from '@backstage/core-components';
import {
  Collapse,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckIcon from '@material-ui/icons/Check';

/**
 * `status` and `note` turn the explainer from documentation into a live pipeline:
 * the numbered stages ARE the stages the page implements, and a page that knows
 * where the reader is can say so. Both are optional — a step without them renders
 * exactly as before, which is what every incumbent caller gets.
 *
 *   done   already behind the reader; recedes, and its number becomes a check
 *   active where they are now; brand-filled and titled in brand
 *   todo   ahead of them; the plain neutral chip
 */
export type HowItWorksStep = {
  title: string;
  body: ReactNode;
  status?: 'done' | 'active' | 'todo';
  /** A live figure for this stage, e.g. "4 queued". Rendered beside the title. */
  note?: ReactNode;
};

const useStyles = makeStyles(theme => ({
  card: { marginBottom: theme.spacing(2.5) },
  // 'plain' variant: a bordered block rather than a card, for pages that open
  // straight onto content instead of a stack of cards (team admin).
  plain: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2.25, 2.5),
    marginBottom: theme.spacing(2.5),
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.02)'
        : 'rgba(0,0,0,0.015)',
  },
  plainHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  plainTitle: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    flexGrow: 1,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  // With a summary beside it, the title keeps its own width and the summary
  // takes the rest, so the summary sits next to the title instead of floating
  // mid-row, and a narrow screen wraps the summary, never the title.
  plainTitleWithSummary: { flexGrow: 0 },
  lead: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    marginBottom: theme.spacing(2),
    // Running text, so it gets a running-text measure.
    maxWidth: '75ch',
  },
  // The plain variant's lead spans the section like the steps grid below it,
  // rather than stopping short of it on wide screens.
  leadPlain: { maxWidth: 'none' },
  steps: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: theme.spacing(2.5),
  },
  step: {
    display: 'flex',
    gap: theme.spacing(1.25),
    alignItems: 'flex-start',
  },
  // Quiet step indicator: numbers orient the sequence but aren't a primary
  // action, so they use a neutral chip rather than the brand fill (which would
  // compete with the page header). Brand color stays on the number itself.
  num: {
    flexShrink: 0,
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    color: theme.palette.primary.main,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.06)',
  },
  // 'done': the number becomes a check, so the state is carried by SHAPE and not
  // only by colour.
  numDone: {
    color: theme.palette.success.dark,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(46,160,67,0.22)'
        : 'rgba(46,160,67,0.14)',
  },
  // 'active': the one stage that gets the brand fill. Nothing else on the
  // explainer competes with it, which is the point.
  numActive: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
  },
  stepTitle: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    marginBottom: theme.spacing(0.25),
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
  },
  // primary.DARK, not primary.main: at 13px this is body text, so it needs
  // 4.5:1, and main measured 3.97:1 against the explainer's tinted surface.
  // On the dark theme primary.dark sinks into the surface (about 2.3:1), so the
  // lighter brand shade carries the same role there.
  stepTitleActive: {
    color:
      theme.palette.type === 'dark'
        ? theme.palette.primary.light
        : theme.palette.primary.dark,
  },
  // A finished stage recedes rather than disappears: still readable, no longer
  // asking for attention.
  stepDone: { opacity: 0.62 },
  // The live figure for a stage. Not wrapped in a live region on purpose -- the
  // pages that pass a note already announce the same change (Team Admin has a
  // cart live region), and two announcements of one event is worse than one.
  stepNote: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '0.02em',
    // Same reason as stepTitleActive, and worse here: 11px on a 6%-black chip
    // measured 3.49:1 with primary.main. Dark theme: see stepTitleActive.
    color:
      theme.palette.type === 'dark'
        ? theme.palette.primary.light
        : theme.palette.primary.dark,
    padding: theme.spacing(0, 0.75),
    borderRadius: 10,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.10)'
        : 'rgba(0,0,0,0.06)',
  },
  stepBody: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  // Shown in the header while collapsed, so a dismissed explainer still carries
  // the promise the page depends on rather than leaving a bare title.
  summary: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    flexGrow: 1,
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}));

type Props = {
  lead: ReactNode;
  steps: HowItWorksStep[];
  /** 'card' (default) sits in a card stack; 'plain' is a bordered block. */
  variant?: 'card' | 'plain';
  /**
   * Make the explainer collapsible and remember the state under this
   * localStorage key. Worth it on pages whose main surface wants the vertical
   * space (the network-policy canvas); omit for an always-expanded explainer.
   */
  collapseKey?: string;
  /**
   * First-visit state when collapseKey has no stored value yet. Defaults to open
   * (every incumbent caller's behavior); analysis surfaces whose explainer should
   * not outrank the data pass false. A stored user choice always wins.
   */
  defaultOpen?: boolean;
  /**
   * One line shown in the header WHILE COLLAPSED. Without it, dismissing the
   * explainer leaves a bare "How it works" title and the page loses the thing it
   * most needs to keep saying. Only rendered when collapsible and closed.
   */
  summary?: ReactNode;
  footer?: ReactNode;
};

/**
 * The numbered "How it works" explainer that sits above a tool page's content
 * (team admin, control panel, network policies).
 */
export const HowItWorks = ({
  lead,
  steps,
  variant = 'card',
  collapseKey,
  defaultOpen = true,
  summary,
  footer,
}: Props) => {
  const classes = useStyles();
  const collapsible = Boolean(collapseKey);
  const [open, setOpen] = useState(() => {
    if (!collapseKey) return true;
    try {
      const stored = localStorage.getItem(collapseKey);
      if (stored === null) return defaultOpen;
      return stored !== '0';
    } catch {
      return defaultOpen;
    }
  });

  // Tracks the content padding separately from `open`: `open` flips
  // synchronously but the content stays mounted for Collapse's exit
  // transition, so dropping the padding on the same tick makes the text jump
  // a frame before it animates away. Padding goes on at enter, off at exited.
  const [padded, setPadded] = useState(open);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (!collapseKey) return;
    try {
      localStorage.setItem(collapseKey, next ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  const toggleButton = collapsible ? (
    <Tooltip title={open ? 'Hide' : 'Show'}>
      <IconButton
        size="small"
        onClick={toggle}
        aria-expanded={open}
        aria-label={open ? 'Hide how it works' : 'Show how it works'}
      >
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    </Tooltip>
  ) : undefined;

  const inner = (
    <>
      <Typography
        className={`${classes.lead} ${
          variant === 'plain' ? classes.leadPlain : ''
        }`}
      >
        {lead}
      </Typography>
      <div className={classes.steps}>
        {steps.map((step, i) => {
          const done = step.status === 'done';
          const active = step.status === 'active';
          return (
            <div
              key={step.title}
              className={`${classes.step}${done ? ` ${classes.stepDone}` : ''}`}
            >
              <span
                className={`${classes.num}${done ? ` ${classes.numDone}` : ''}${
                  active ? ` ${classes.numActive}` : ''
                }`}
              >
                {done ? <CheckIcon style={{ fontSize: 15 }} /> : i + 1}
                {/* The stage's state in words, for anyone who cannot see the
                    check or the fill. */}
                {step.status && (
                  <span className={classes.srOnly}>
                    {done
                      ? `Step ${i + 1}, done.`
                      : `Step ${i + 1}, ${active ? 'current' : 'not started'}.`}
                  </span>
                )}
              </span>
              <div>
                <div
                  className={`${classes.stepTitle}${
                    active ? ` ${classes.stepTitleActive}` : ''
                  }`}
                >
                  {step.title}
                  {step.note && (
                    <span className={classes.stepNote}>{step.note}</span>
                  )}
                </div>
                <div className={classes.stepBody}>{step.body}</div>
              </div>
            </div>
          );
        })}
      </div>
      {footer}
    </>
  );

  const body = collapsible ? (
    <Collapse
      in={open}
      unmountOnExit
      onEnter={() => setPadded(true)}
      onExited={() => setPadded(false)}
    >
      {inner}
    </Collapse>
  ) : (
    inner
  );

  if (variant === 'plain') {
    return (
      <div className={classes.plain}>
        <div className={classes.plainHeader}>
          <Typography
            component="h2"
            className={`${classes.plainTitle}${
              collapsible && !open && summary
                ? ` ${classes.plainTitleWithSummary}`
                : ''
            }`}
          >
            How it works
          </Typography>
          {collapsible && !open && summary && (
            <Typography className={classes.summary}>{summary}</Typography>
          )}
          {toggleButton}
        </div>
        {body}
      </div>
    );
  }

  const cardAction =
    collapsible && !open && summary ? (
      <>
        <Typography className={classes.summary}>{summary}</Typography>
        {toggleButton}
      </>
    ) : (
      toggleButton
    );

  return (
    <InfoCard
      title="How it works"
      className={classes.card}
      // Fully collapsed: drop the content padding too, so the card is
      // header-only (see the `padded` note above for why not `open`).
      noPadding={collapsible && !padded}
      action={cardAction}
    >
      {body}
    </InfoCard>
  );
};
